<script setup lang="ts">
import { computed } from 'vue'
import type { Seller, RelatedFileCount, SellerAnnotation } from '@/types/api'
import { useInitMasters } from '@/api/queries'
import { openExternal } from '@/utils/links'
import { runRelatedFileBat } from '@/utils/localExec'

const props = defineProps<{
  seller: Seller | null | undefined
  /** 関連ファイル数バッジ(JudgeViewから渡す。未計測ならnull) */
  fileCount?: RelatedFileCount | null
  /** セラーコメント(VB.NET版のセラーコメント欄に相当するやり取りスレッド) */
  annotations?: SellerAnnotation[] | null
}>()

const { data: masters } = useInitMasters()

function formatSellerId(id: string | null | undefined) {
  if (!id) return ''
  return id.startsWith('SSC-') ? id : `SSC-${id}`
}

/** バッジのツールチップ用。「いつ時点の件数か」を示す */
function formatBadgeDate(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

// ---- セラーコメント ----
// VB.NET版のセラーコメント欄に相当。getOfferDetail は以前からデータを返していたが
// Web版では未表示だったため、ここで描画する。
// 新しいスレッド順(ThreadSeq DESC)で返るので、そのまま並べる。
const comments = computed(() =>
  (props.annotations ?? []).filter((a) => (a.ResponseContents ?? '').trim() !== ''),
)

// セルモンプランコード → プラン名 (VB.NET版 mDicSalmonplanMaster(code).SalMonPlanName 相当)
const planName = computed(() => {
  const code = props.seller?.SalMonPlanCode
  if (!code) return '-'
  const plan = masters.value?.planMaster.find((p) => p.SalMonPlanCode === code)
  return plan?.SalMonPlanName ?? code
})

// DQセラー確認ページ (社内HTTP)
function openDqSeller() {
  if (props.seller?.SellerId) {
    openExternal(`http://192.168.10.47/query/5200?SellerId=${props.seller.SellerId}`)
  }
}

// 関連ファイル(セラー): VB.NET版は \\Hanami\...\OpenSeller.bat をSellerId引数で起動。
// ブラウザからbatは起動できないため、パスをクリップボードにコピーする。
function copySellerFolder() {
  if (props.seller?.SellerId) {
    runRelatedFileBat('OpenSeller.bat', props.seller.SellerId)
  }
}
</script>

<template>
  <div class="seller-bar">
    <div class="seller-bar-head">
      <div class="section-title" style="margin-bottom:0;">
        <svg class="icon" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        セラー情報
      </div>
      <div class="seller-links" v-if="seller">
        <a class="qlink" @click="openDqSeller">
          <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          セラー確認ページ
        </a>
        <a class="qlink" @click="copySellerFolder" title="関連ファイルのフォルダを開きます(拡張機能が無い場合はパスをコピーします)">
          <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
          関連ファイル（セラー）
          <!-- 関連ファイル数(FileCounter.exe がDBへ書いた件数)。未計測なら出さない -->
          <span
            v-if="fileCount"
            class="file-badge"
            :class="{ zero: fileCount.fileCount === 0 }"
            :title="`関連ファイル ${fileCount.fileCount} 件`
              + (fileCount.updateDate ? `（${formatBadgeDate(fileCount.updateDate)} 時点）` : '')"
          >{{ fileCount.fileCount }}</span>
        </a>
      </div>
    </div>
    <div v-if="seller" class="seller-bar-grid">
      <div><span class="k">セラーID</span><span class="v"><span class="chip-id">{{ formatSellerId(seller.SellerIdAlias) }}</span></span></div>
      <div><span class="k">会社名</span><span class="v" :title="seller.CompanyName ?? ''">{{ seller.CompanyName }}</span></div>
      <div><span class="k">責任者</span><span class="v" :title="seller.SellerName ?? ''">{{ seller.SellerName }}</span></div>
      <div><span class="k">プラン</span><span class="v"><span class="plan-badge">{{ planName }}</span></span></div>
    </div>

    <!-- セラーコメント -->
    <div v-if="seller && comments.length" class="seller-comments">
      <div class="seller-comments-head">セラーコメント（{{ comments.length }}）</div>
      <div v-for="c in comments" :key="`${c.ThreadSeq}-${c.AnnotationSeq}`" class="seller-comment">
        <div class="seller-comment-body">{{ c.ResponseContents }}</div>
      </div>
    </div>
    <div v-else class="empty-state" style="padding:12px 0;">商品を選択してください</div>
  </div>
</template>
